import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Flame, Zap, Wind } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkoutLibrary = () => {
  const [drills, setDrills] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDrills();
  }, []);

  const fetchDrills = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/drills`, { headers });
      setDrills(response.data);
    } catch (error) {
      console.error('Error fetching drills:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      stamina: Flame,
      explosiveness: Zap,
      agility: Wind
    };
    return icons[category] || Flame;
  };

  const getCategoryColor = (category) => {
    const colors = {
      stamina: 'bg-primary/10 text-primary border-primary/30',
      explosiveness: 'bg-secondary/10 text-secondary border-secondary/30',
      agility: 'bg-accent/10 text-accent border-accent/30'
    };
    return colors[category] || 'bg-primary/10 text-primary border-primary/30';
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const filteredDrills = activeCategory === 'all' 
    ? drills 
    : drills.filter(d => d.category === activeCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-primary text-xl font-heading">Loading library...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 md:p-8 lg:p-12">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-white tracking-tighter-xl">
            WORKOUT LIBRARY
          </h1>
          <p className="text-muted-foreground mt-2">Browse conditioning drills by category</p>
        </div>

        {/* Category Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
          <TabsList className="grid grid-cols-4 w-full md:w-auto" data-testid="category-tabs">
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="stamina" data-testid="tab-stamina">Stamina</TabsTrigger>
            <TabsTrigger value="explosiveness" data-testid="tab-explosiveness">Explosiveness</TabsTrigger>
            <TabsTrigger value="agility" data-testid="tab-agility">Agility</TabsTrigger>
          </TabsList>

          <TabsContent value={activeCategory} className="mt-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="drill-grid">
              {filteredDrills.map((drill) => {
                const Icon = getCategoryIcon(drill.category);
                return (
                  <Card 
                    key={drill.id} 
                    className="bg-card border-border hover:border-primary/50 transition-colors group"
                    data-testid={`drill-card-${drill.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between mb-3">
                        <div className={`w-12 h-12 rounded-sm border flex items-center justify-center ${getCategoryColor(drill.category)}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <Badge className={`uppercase text-xs font-bold ${getCategoryColor(drill.category)} border`}>
                          {drill.category}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-heading font-bold text-white group-hover:text-primary transition-colors">
                        {drill.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-gray-300">{drill.description}</p>
                      
                      <div className="grid grid-cols-2 gap-3 p-3 bg-background rounded-sm border border-border">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Work Time</p>
                          <p className="text-sm font-mono font-bold text-primary">{formatTime(drill.duration_seconds)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Rest Time</p>
                          <p className="text-sm font-mono font-bold text-secondary">{formatTime(drill.rest_seconds)}</p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase">Instructions:</p>
                        <ul className="space-y-1">
                          {drill.instructions.slice(0, 3).map((instruction, idx) => (
                            <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
                              <span className="text-primary mt-0.5">•</span>
                              <span>{instruction}</span>
                            </li>
                          ))}
                          {drill.instructions.length > 3 && (
                            <li className="text-xs text-muted-foreground">
                              +{drill.instructions.length - 3} more steps...
                            </li>
                          )}
                        </ul>
                      </div>

                      {drill.common_mistakes && drill.common_mistakes.length > 0 && (
                        <div className="pt-2 border-t border-red-500/10" data-testid={`drill-mistake-preview-${drill.id}`}>
                          <p className="text-[10px] font-mono uppercase tracking-wider text-red-400/80 mb-1">
                            ✕ Common Mistake
                          </p>
                          <p className="text-xs text-gray-400 italic line-clamp-2">{drill.common_mistakes[0]}</p>
                          {drill.common_mistakes.length > 1 && (
                            <p className="text-[10px] text-muted-foreground mt-1">
                              +{drill.common_mistakes.length - 1} more
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {filteredDrills.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                No drills found in this category.
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          {['stamina', 'explosiveness', 'agility'].map(category => {
            const count = drills.filter(d => d.category === category).length;
            const Icon = getCategoryIcon(category);
            return (
              <Card key={category} className="bg-card border-border" data-testid={`summary-${category}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-sm border flex items-center justify-center ${getCategoryColor(category)}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-black text-white">{count}</p>
                    <p className="text-xs text-muted-foreground uppercase">{category}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WorkoutLibrary;